import * as React from 'react';
import type { ViolationKind } from './ViolationBadge';

export type Violation = {
  kind: ViolationKind;
  message: string;
  rect: DOMRect;
};

const THIRD_PARTY_SELECTORS =
  '.recharts-wrapper, .react-flow, [data-radix-popper-content-wrapper], [data-third-party]';

/** Matches arbitrary Tailwind color utilities: bg-[#xxx], text-[#xxx], border-[#xxx], etc. */
const ARBITRARY_COLOR_REGEX =
  /\b(bg|text|border|ring|from|to|via|fill|stroke|outline)-\[[^\]]+\]/g;

function isInsideThirdParty(el: Element): boolean {
  return el.closest(THIRD_PARTY_SELECTORS) !== null;
}

function hasDataComponentAncestor(el: Element): boolean {
  return el.closest('[data-component]') !== null;
}

function getStructuralViolations(root: Document): Violation[] {
  const out: Violation[] = [];
  const body = root.body ?? root.documentElement;

  // Detect native select elements that should use the Select primitive
  for (const el of body.querySelectorAll('select')) {
    if (isInsideThirdParty(el) || hasDataComponentAncestor(el)) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      out.push({ kind: 'structural', message: 'Native element — use Select primitive', rect });
    }
  }

  // Detect native date/time inputs that should use DatePicker/TimePicker
  for (const el of body.querySelectorAll('input[type="date"], input[type="time"]')) {
    if (isInsideThirdParty(el) || hasDataComponentAncestor(el)) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const label = el.getAttribute('type') === 'date' ? 'DatePicker' : 'TimePicker';
      out.push({ kind: 'structural', message: `Native element — use ${label}`, rect });
    }
  }

  return out;
}

function getTokenViolations(root: Document): Violation[] {
  const out: Violation[] = [];
  const all = root.body?.querySelectorAll('*') ?? [];
  for (const el of all) {
    if (!(el instanceof HTMLElement)) continue;
    if (isInsideThirdParty(el)) continue;
    const cn = el.className;
    if (typeof cn !== 'string') continue;
    const match = cn.match(ARBITRARY_COLOR_REGEX);
    if (match) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        out.push({
          kind: 'token',
          message: `Arbitrary Tailwind: ${match.slice(0, 2).join(', ')}${match.length > 2 ? '…' : ''}`,
          rect
        });
      }
    }
  }
  return out;
}

export function scanForViolations(root: Document): { structural: Violation[]; token: Violation[] } {
  const structural = getStructuralViolations(root);
  const token = getTokenViolations(root);
  return { structural, token };
}

const THROTTLE_MS = 500;

/**
 * Hook that runs a throttled MutationObserver to scan the document for violations.
 * Returns { structural, token } arrays. Only runs when active and NODE_ENV !== 'production'.
 */
export function useViolationScan(active: boolean): { structural: Violation[]; token: Violation[] } {
  const [structural, setStructural] = React.useState<Violation[]>([]);
  const [token, setToken] = React.useState<Violation[]>([]);

  const runScan = React.useCallback(() => {
    if (typeof document === 'undefined') return;
    const { structural: s, token: t } = scanForViolations(document);
    setStructural(s);
    setToken(t);
  }, []);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !active) {
      setStructural([]);
      setToken([]);
      return;
    }
    runScan();
    let last = 0;
    let scheduled: ReturnType<typeof setTimeout> | null = null;
    const observer = new MutationObserver(() => {
      const now = Date.now();
      const elapsed = now - last;
      if (elapsed >= THROTTLE_MS) {
        last = now;
        runScan();
      } else if (scheduled === null) {
        scheduled = setTimeout(() => {
          scheduled = null;
          last = Date.now();
          runScan();
        }, THROTTLE_MS - elapsed);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    const onScrollResize = () => {
      if (Date.now() - last >= THROTTLE_MS) {
        last = Date.now();
        runScan();
      }
    };
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      observer.disconnect();
      if (scheduled !== null) clearTimeout(scheduled);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [active, runScan]);

  return { structural, token };
}
